import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil, Trash2, ArrowLeft, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { frontendDb as db } from '../../lib/frontend-db';
import { slugifyTitle } from '../../lib/slug';
import type { PosterItem, PosterImage } from '../../lib/frontend-db';

const SortableImageItem = ({ image, onEdit, onDelete }: {
  image: PosterImage;
  onEdit: (image: PosterImage) => void;
  onDelete: (id: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-lg shadow-md p-4"
    >
      <div className="flex justify-between items-start mb-2">
        <div
          {...attributes}
          {...listeners}
          className="text-gray-400 hover:text-gray-600 cursor-grab"
        >
          <GripVertical size={20} />
        </div>
        <h3 className="font-semibold flex-grow px-2">{image.title}</h3>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(image)}
            className="p-1 text-green-700 hover:text-green-800"
          >
            <Pencil size={20} />
          </button>
          <button
            onClick={() => onDelete(image.id)}
            className="p-1 text-red-600 hover:text-red-700"
          >
            <Trash2 size={20} />
          </button>
        </div>
      </div>
      <img
        src={image.image_url}
        alt={image.title}
        className="w-full h-48 object-cover rounded"
      />
    </div>
  );
};

const PosterImagesPage = () => {
  const { posterId } = useParams();
  const navigate = useNavigate();
  const [poster, setPoster] = useState<PosterItem | null>(null);
  const [images, setImages] = useState<PosterImage[]>([]);
  const [title, setTitle] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [editingImage, setEditingImage] = useState<PosterImage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (posterId) {
      loadPosterAndImages();
    }
  }, [posterId]);

  const loadPosterAndImages = async () => {
    if (!posterId) return;
    
    try {
      const [posterData, imagesData] = await Promise.all([
        db.getPoster(posterId),
        db.getPosterImages(posterId)
      ]);

      if (posterData) {
        setPoster(posterData);
        setImages(imagesData);
      } else {
        setError('Poster nicht gefunden');
      }
    } catch (error) {
      console.error('Error loading poster and images:', error);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!posterId || !poster) return;

    setLoading(true);
    setError(null);

    try {
      let imageUrl = editingImage?.image_url || '';

      if (selectedImage) {
        try {
          console.log('📤 Starting image upload for poster image');
          imageUrl = await db.storeImage(selectedImage);
          console.log('✅ Image uploaded successfully:', imageUrl);
        } catch (imageError) {
          console.error('❌ Error uploading image:', imageError);
          setError('Fehler beim Hochladen des Bildes: ' + (imageError as Error).message);
          return;
        }
      }

      if (editingImage) {
        try {
          await db.updatePosterImage(editingImage.id, {
            ...editingImage,
            title,
            image_url: imageUrl,
          });
          console.log('✅ Poster image updated successfully');
        } catch (updateError) {
          console.error('❌ Error updating poster image:', updateError);
          setError('Fehler beim Aktualisieren des Bildes: ' + (updateError as Error).message);
          return;
        }
      } else {
        try {
          await db.createPosterImage({
            poster_id: posterId,
            title,
            image_url: imageUrl,
            order_index: images.length,
          });
          console.log('✅ Poster image created successfully');
        } catch (createError) {
          console.error('❌ Error creating poster image:', createError);
          setError('Fehler beim Erstellen des Bildes: ' + (createError as Error).message);
          return;
        }
      }

      resetForm();
      window.location.reload();
    } catch (error) {
      console.error('❌ Unexpected error saving image:', error);
      setError('Unerwarteter Fehler beim Speichern des Bildes: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Möchten Sie dieses Bild wirklich löschen?')) {
      return;
    }

    try {
      await db.deletePosterImage(id);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting image:', error);
      setError('Fehler beim Löschen des Bildes');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = images.findIndex((item) => item.id === active.id);
    const newIndex = images.findIndex((item) => item.id === over.id);

    const reorderedImages = arrayMove(images, oldIndex, newIndex);

    // Update order_index for all items
    const updatedItems = reorderedImages.map((item, index) => ({
      ...item,
      order_index: index
    }));

    setImages(updatedItems);

    // Update the order in the database
    try {
      for (const item of updatedItems) {
        await db.updatePosterImage(item.id, { order_index: item.order_index });
      }
    } catch (error) {
      console.error('Error updating image order:', error);
      setError('Fehler beim Aktualisieren der Reihenfolge');
      loadPosterAndImages(); // Reload original order on error
    }
  };

  const resetForm = () => {
    setEditingImage(null);
    setTitle('');
    setSelectedImage(null);
    setPreviewUrl('');
  };

  if (loading) {
    return <div className="animate-pulse">Lädt...</div>;
  }

  if (!poster) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Poster nicht gefunden
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/dashboard/posters')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft size={20} />
          Zurück zu Postern
        </button>
        <h1 className="text-2xl font-bold">Bilder für: {poster.title}</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">
          {editingImage ? 'Bild bearbeiten' : 'Neues Bild hinzufügen'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Titel</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Bild</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-1 block w-full"
              required={!editingImage?.image_url}
            />
            {(previewUrl || editingImage?.image_url) && (
              <div className="mt-2">
                <img
                  src={previewUrl || editingImage?.image_url}
                  alt="Vorschau"
                  className="w-full max-h-48 object-cover rounded"
                />
              </div>
            )}
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-700 text-white py-2 px-4 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Wird gespeichert...' : (editingImage ? 'Aktualisieren' : 'Hinzufügen')}
            </button>
            {editingImage && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
              >
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={images.map(img => img.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image) => (
              <SortableImageItem
                key={image.id}
                image={image}
                onEdit={(image) => {
                  setEditingImage(image);
                  setTitle(image.title);
                  setPreviewUrl(image.image_url);
                }}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default PosterImagesPage;

